import { inject, PLATFORM_ID } from '@angular/core';
import { TransferState, makeStateKey } from '@angular/core';
import { isPlatformServer } from '@angular/common';
import { ResolveFn } from '@angular/router';
import { catchError, map, of, retry, tap, timeout } from 'rxjs';
import { PropertiesService } from '../modules/api';
import { ApiError } from '../modules/api/core/ApiError';
import type { PropertyResponse } from '../modules/api';

/**
 * Result of the property detail resolver.
 */
export interface PropertyDetailResolveResult {
  /** Resolved property, or null if it could not be loaded. */
  property: PropertyResponse | null;
  /** True when the backend explicitly returned 404 (property does not exist). */
  notFound: boolean;
}

/**
 * Route resolver for /property/:id.
 *
 * Loads the property on the server before Angular renders the component.
 * This is the single source of data for SSR: when it resolves successfully,
 * the page is emitted to crawlers WITH full listing content (title,
 * description, price, photos, JSON-LD). When the property is genuinely
 * missing, `notFound` is set to true so the component can render the proper
 * 404 experience instead of a "Loading..." shell.
 *
 * To keep SSR content reliable under transient backend hiccups, each attempt
 * is bounded by a timeout and retried a few times before giving up.
 *
 * The resolved result is stored in TransferState on the server and replayed
 * synchronously on the client. This lets the hydrated component read it from
 * the route snapshot before its first render, avoiding a refetch/loading
 * flash. The built-in HTTP transfer cache is not relied upon because it does
 * not cache requests carrying an Authorization header (logged-in users).
 */
export const propertyDetailResolver: ResolveFn<PropertyDetailResolveResult> = (route) => {
  const api = inject(PropertiesService);
  const transferState = inject(TransferState);
  const platformId = inject(PLATFORM_ID);
  const id = route.paramMap.get('id');
  if (!id) {
    return of({ property: null, notFound: true });
  }

  const cacheKey = makeStateKey<PropertyDetailResolveResult>(`property-detail-${id}`);

  // Client: reuse the SSR-resolved result synchronously, no refetch/loading flash.
  if (transferState.hasKey(cacheKey)) {
    const cached = transferState.get<PropertyDetailResolveResult>(
      cacheKey,
      { property: null, notFound: false },
    );
    transferState.remove(cacheKey);
    return of(cached);
  }

  return api.getPropertyApiPropertiesPropertyIdGet(id).pipe(
    timeout(5000),
    retry({ count: 2, delay: 150 }),
    map((property) => ({ property, notFound: false })),
    tap((result) => {
      if (isPlatformServer(platformId)) {
        transferState.set(cacheKey, result);
      }
    }),
    catchError((err: unknown) => {
      const notFound = err instanceof ApiError && err.status === 404;
      if (notFound && isPlatformServer(platformId)) {
        transferState.set(cacheKey, { property: null, notFound: true });
      }
      return of({ property: null, notFound });
    }),
  );
};